import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { posService } from '../../../lib/posService';

const InventoryManagement = ({ products, categories, gymId, onProductsChange }) => {
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    barcode: '',
    alu: '',
    price: '',
    cost: '',
    stock_quantity: '',
    low_stock_threshold: '10'
  });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product?.name || '',
      description: product?.description || '',
      category_id: product?.category_id || '',
      barcode: product?.barcode || '',
      alu: product?.alu || '',
      price: product?.price || '',
      cost: product?.cost || '',
      stock_quantity: product?.stock_quantity || '',
      low_stock_threshold: product?.low_stock_threshold || '10'
    });
    setIsAddingProduct(true);
  };

  const handleSave = async () => {
    if (!formData?.name || !formData?.price) {
      alert('Please fill in required fields');
      return;
    }

    setSaving(true);
    const productData = {
      ...formData,
      price: parseFloat(formData?.price),
      cost: formData?.cost ? parseFloat(formData?.cost) : null,
      stock_quantity: parseInt(formData?.stock_quantity) || 0,
      low_stock_threshold: parseInt(formData?.low_stock_threshold) || 10
    };

    const { error } = editingProduct
      ? await posService?.updateProduct(editingProduct?.id, productData)
      : await posService?.createProduct(gymId, productData);

    if (error) {
      alert(error?.message || 'Failed to save product');
    } else {
      setIsAddingProduct(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        category_id: '',
        barcode: '',
        alu: '',
        price: '',
        cost: '',
        stock_quantity: '',
        low_stock_threshold: '10'
      });
      onProductsChange();
    }
    setSaving(false);
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    const { error } = await posService?.deleteProduct(productId);
    if (error) {
      alert(error?.message || 'Failed to delete product');
    } else {
      onProductsChange();
    }
  };

  const handleExportCSV = () => {
    if (!products || products?.length === 0) {
      alert('No products to export');
      return;
    }

    const headers = ['Name', 'Description', 'Category', 'Barcode', 'ALU', 'Price', 'Cost', 'Stock Quantity', 'Low Stock Threshold'];
    const rows = products?.map(product => [
      product?.name || '',
      product?.description || '',
      product?.category?.name || '',
      product?.barcode || '',
      product?.alu || '',
      product?.price || '0',
      product?.cost || '0',
      product?.stock_quantity || '0',
      product?.low_stock_threshold || '10'
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `inventory_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleDownloadTemplate = () => {
    const headers = ['Name', 'Description', 'Category', 'Barcode', 'ALU', 'Price', 'Cost', 'Stock Quantity', 'Low Stock Threshold'];
    const sampleRows = [
      ['Protein Powder', 'Whey protein supplement', 'Supplements', '123456789', 'PP001', '49.99', '25.00', '50', '10'],
      ['Gym T-Shirt', 'Cotton gym t-shirt', 'Apparel', '987654321', 'TS001', '24.99', '12.00', '100', '20'],
      ['Water Bottle', 'Reusable water bottle', 'Accessories', '456789123', 'WB001', '14.99', '7.00', '75', '15']
    ];

    const csvContent = [
      headers?.join(','),
      ...sampleRows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', 'inventory_template.csv');
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleImportCSV = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResults(null);

    try {
      const text = await file?.text();
      const lines = text?.split('\n')?.filter(line => line?.trim());
      
      if (lines?.length < 2) {
        alert('CSV file is empty or invalid');
        setImporting(false);
        return;
      }

      const headers = lines?.[0]?.split(',')?.map(h => h?.replace(/"/g, '')?.trim());
      const dataLines = lines?.slice(1);

      const results = {
        total: dataLines?.length,
        success: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < dataLines?.length; i++) {
        const values = dataLines?.[i]?.split(',')?.map(v => v?.replace(/"/g, '')?.trim());
        
        const productData = {
          name: values?.[0] || '',
          description: values?.[1] || '',
          barcode: values?.[3] || '',
          alu: values?.[4] || '',
          price: parseFloat(values?.[5]) || 0,
          cost: parseFloat(values?.[6]) || 0,
          stock_quantity: parseInt(values?.[7]) || 0,
          low_stock_threshold: parseInt(values?.[8]) || 10
        };

        // Find category by name
        const categoryName = values?.[2];
        if (categoryName) {
          const category = categories?.find(cat => cat?.name?.toLowerCase() === categoryName?.toLowerCase());
          if (category) {
            productData.category_id = category?.id;
          }
        }

        if (!productData?.name || productData?.price <= 0) {
          results.failed++;
          results?.errors?.push(`Row ${i + 2}: Missing required fields (Name or Price)`);
          continue;
        }

        const { error } = await posService?.createProduct(gymId, productData);
        
        if (error) {
          results.failed++;
          results?.errors?.push(`Row ${i + 2}: ${error?.message || 'Failed to import'}`);
        } else {
          results.success++;
        }
      }

      setImportResults(results);
      onProductsChange();
    } catch (error) {
      alert('Failed to parse CSV file: ' + error?.message);
    }

    setImporting(false);
    event.target.value = '';
  };

  const filteredProducts = products?.filter(product =>
    product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    product?.barcode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    product?.alu?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const lowStockProducts = products?.filter(p => p?.stock_quantity <= p?.low_stock_threshold);

  return (
    <div className="space-y-6">
      {/* Low Stock Alert */}
      {lowStockProducts?.length > 0 && (
        <div className="bg-warning/10 border border-warning rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <h3 className="font-semibold text-foreground">Low Stock Alert</h3>
          </div>
          <div className="space-y-2">
            {lowStockProducts?.slice(0, 5)?.map(product => (
              <div key={product?.id} className="flex items-center justify-between text-sm">
                <span className="text-foreground">{product?.name}</span>
                <span className="font-medium text-warning data-text">{product?.stock_quantity} units</span>
              </div>
            ))}
            {lowStockProducts?.length > 5 && (
              <p className="text-xs text-muted-foreground">+{lowStockProducts?.length - 5} more items</p>
            )}
          </div>
        </div>
      )}
      <div className="bg-card rounded-xl shadow-md border border-border">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Inventory Management</h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                iconName="Download"
                iconPosition="left"
                size="sm"
              >
                Template
              </Button>
              <Button
                variant="outline"
                onClick={handleExportCSV}
                iconName="Upload"
                iconPosition="left"
                size="sm"
              >
                Export
              </Button>
              <label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  disabled={importing}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  as="span"
                  iconName="Download"
                  iconPosition="left"
                  size="sm"
                  disabled={importing}
                >
                  {importing ? 'Importing...' : 'Import'}
                </Button>
              </label>
              <Button
                onClick={() => {
                  setIsAddingProduct(true);
                  setEditingProduct(null);
                }}
                iconName="Plus"
                iconPosition="left"
                size="sm"
              >
                Add Product
              </Button>
            </div>
          </div>
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>

        {importResults && (
          <div className={`p-4 border-b border-border ${
            importResults?.failed === 0 ? 'bg-success/10' : 'bg-warning/10'
          }`}>
            <div className="flex items-start gap-3">
              <Icon 
                name={importResults?.failed === 0 ? 'CheckCircle' : 'AlertTriangle'} 
                size={20} 
                color={importResults?.failed === 0 ? 'var(--color-success)' : 'var(--color-warning)'} 
              />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Import Results</h3>
                <div className="text-sm space-y-1">
                  <p className="text-foreground">Total: {importResults?.total} | Success: {importResults?.success} | Failed: {importResults?.failed}</p>
                  {importResults?.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium text-foreground mb-1">Errors:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        {importResults?.errors?.slice(0, 5)?.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                        {importResults?.errors?.length > 5 && (
                          <li>+{importResults?.errors?.length - 5} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setImportResults(null)}
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        )}

        {isAddingProduct ? (
          <div className="p-6 border-b border-border bg-muted/20">
            <h3 className="font-semibold text-foreground mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Product Name"
                type="text"
                placeholder="Enter product name"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
              />
              <Select
                label="Category"
                value={formData?.category_id}
                onChange={(value) => setFormData({ ...formData, category_id: value })}
                options={[
                  { value: '', label: 'Select category' },
                  ...categories?.map(cat => ({ value: cat?.id, label: cat?.name }))
                ]}
              />
              <Input
                label="Barcode"
                type="text"
                placeholder="Enter barcode"
                value={formData?.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e?.target?.value })}
              />
              <Input
                label="ALU (Article Lookup Unit)"
                type="text"
                placeholder="Enter ALU"
                value={formData?.alu}
                onChange={(e) => setFormData({ ...formData, alu: e?.target?.value })}
              />
              <Input
                label="Price"
                type="number"
                placeholder="0.00"
                value={formData?.price}
                onChange={(e) => setFormData({ ...formData, price: e?.target?.value })}
                required
              />
              <Input
                label="Cost"
                type="number"
                placeholder="0.00"
                value={formData?.cost}
                onChange={(e) => setFormData({ ...formData, cost: e?.target?.value })}
              />
              <Input
                label="Stock Quantity"
                type="number"
                placeholder="0"
                value={formData?.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e?.target?.value })}
              />
              <Input
                label="Low Stock Threshold"
                type="number"
                placeholder="10"
                value={formData?.low_stock_threshold}
                onChange={(e) => setFormData({ ...formData, low_stock_threshold: e?.target?.value })}
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="Enter product description"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingProduct(false);
                  setEditingProduct(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                iconName="Save"
                iconPosition="left"
              >
                {saving ? 'Saving...' : 'Save Product'}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Barcode/ALU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProducts?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <p className="text-muted-foreground">No products found</p>
                  </td>
                </tr>
              ) : (
                filteredProducts?.map(product => (
                  <tr key={product?.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{product?.name}</p>
                        <p className="text-xs text-muted-foreground">{product?.category?.name || 'Uncategorized'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-muted-foreground">
                        {product?.barcode && <p>BC: {product?.barcode}</p>}
                        {product?.alu && <p>ALU: {product?.alu}</p>}
                        {!product?.barcode && !product?.alu && <p>-</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground data-text">${product?.price?.toFixed(2)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium data-text ${
                          product?.stock_quantity <= product?.low_stock_threshold
                            ? 'text-warning' :'text-foreground'
                        }`}>
                          {product?.stock_quantity}
                        </span>
                        {product?.stock_quantity <= product?.low_stock_threshold && (
                          <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-1 rounded hover:bg-muted transition-colors"
                        >
                          <Icon name="Edit" size={16} color="var(--color-primary)" />
                        </button>
                        <button
                          onClick={() => handleDelete(product?.id)}
                          className="p-1 rounded hover:bg-error/10 transition-colors"
                        >
                          <Icon name="Trash2" size={16} color="var(--color-error)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;